import { Button } from "@mantine/core";
import { useEffect, useState } from "react";
import { type SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import type { ApiResponse } from "../../../api";
import { ModalFooter } from "../../../mantine/components/ModalFooter/ModalFooter";
import { useToast } from "../../../mantine/notifications/useToast";
import type { WizardFormModeProps } from "../../../types/wizard.types";
import type { MeteringOrMarketLocation } from "../../../utils/backend-types";
import { applyHighlightingToObjectFormFieldData } from "../../../utils/react-hook-form/applyHighlightingToObjectFormFieldData";
import { FormFieldController } from "../../BuildingBlocks/Forms/Controllers/FormFieldController";
import { setErrorsFromResponseData } from "../../BuildingBlocks/Forms/utils/setErrorsFromResponseData";
import { METERING_LOCATION_FORM_FIELD_DATA } from "./MeteringLocationForm.constants";

const DEFAULT_VALUES: Partial<MeteringOrMarketLocation> = {
  medium: METERING_LOCATION_FORM_FIELD_DATA.medium.defaultValue,
  frequency: METERING_LOCATION_FORM_FIELD_DATA.frequency.defaultValue,
  measurementType:
    METERING_LOCATION_FORM_FIELD_DATA.measurementType.defaultValue
};

type MeteringLocationFormProps = {
  showStaffView?: boolean;
  onCancel: () => void;
  onChangeNumErrors: (numErrors: number) => void;
  onChangeNumHighlightedFields: (numHighlightedFields: number) => void;
  onSubmit: (data: MeteringOrMarketLocation) => Promise<ApiResponse>;
} & WizardFormModeProps<MeteringOrMarketLocation>;

function MeteringLocationForm({
  showStaffView,
  onCancel,
  onChangeNumErrors,
  onChangeNumHighlightedFields,
  onSubmit,
  ...modeProps
}: MeteringLocationFormProps) {
  const { showErrorToast } = useToast();
  const {
    control,
    formState: { errors },
    handleSubmit,
    reset,
    setError,
    watch
  } = useForm<MeteringOrMarketLocation>({
    defaultValues:
      modeProps.mode === "edit" ? modeProps.wizardObject : DEFAULT_VALUES
  });
  const { formFieldData, numHighlightedFields } =
    applyHighlightingToObjectFormFieldData(
      {
        ...METERING_LOCATION_FORM_FIELD_DATA,
        medium: {
          ...METERING_LOCATION_FORM_FIELD_DATA.medium,
          disabled: modeProps.mode === "edit"
        },
        measurementType: {
          ...METERING_LOCATION_FORM_FIELD_DATA.measurementType,
          disabled: modeProps.mode === "edit"
        },
        frequency: {
          ...METERING_LOCATION_FORM_FIELD_DATA.frequency,
          disabled: modeProps.mode === "edit"
        }
      },
      watch,
      modeProps.mode === "edit" ? modeProps : undefined
    );
  const { t } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    onChangeNumErrors(Object.keys(errors).length);
  }, [errors, onChangeNumErrors]);

  useEffect(() => {
    onChangeNumHighlightedFields(numHighlightedFields);
  }, [numHighlightedFields, onChangeNumHighlightedFields]);

  const onValidSubmit: SubmitHandler<MeteringOrMarketLocation> = async (
    data
  ) => {
    try {
      const response = await onSubmit(data);
      return response;
    } catch (error) {
      setErrorsFromResponseData<MeteringOrMarketLocation>(
        showErrorToast,
        error,
        watch(),
        setError,
        t("errors.UnknownError")
      );
    }
  };

  const submitCallback = handleSubmit(onValidSubmit);

  async function handleClickSubmit() {
    setIsSubmitting(true);

    try {
      await submitCallback();
    } finally {
      setIsSubmitting(false);
    }
  }

  function handleClickCancel() {
    reset();
    onCancel();
  }

  return (
    <form className="MeteringLocationForm" onSubmit={submitCallback}>
      <FormFieldController
        control={control}
        data={formFieldData.number}
        error={errors.number}
      />

      <FormFieldController
        control={control}
        data={formFieldData.customLabel}
        error={errors.customLabel}
      />

      <FormFieldController
        control={control}
        data={formFieldData.meteringServiceProvider}
        error={errors.meteringServiceProvider}
      />

      <FormFieldController
        control={control}
        data={formFieldData.medium}
        error={errors.medium}
      />

      <FormFieldController
        control={control}
        data={formFieldData.measurementType}
        error={errors.measurementType}
      />

      <FormFieldController
        control={control}
        data={formFieldData.frequency}
        error={errors.frequency}
      />

      {showStaffView && (
        <FormFieldController
          control={control}
          data={formFieldData.commissioning}
          error={errors.commissioning}
        />
      )}

      {showStaffView && (
        <FormFieldController
          control={control}
          data={formFieldData.decommissioning}
          error={errors.decommissioning}
        />
      )}

      {showStaffView && (
        <FormFieldController
          control={control}
          data={formFieldData.status}
          error={errors.status}
        />
      )}

      {showStaffView && (
        <FormFieldController
          control={control}
          data={formFieldData.comment}
          error={errors.comment}
        />
      )}

      <ModalFooter>
        <Button variant="default" onClick={handleClickCancel}>
          Abbrechen
        </Button>
        <Button
          color="secondary"
          loading={isSubmitting}
          onClick={handleClickSubmit}
        >
          {modeProps.mode === "edit" ? "Speichern" : "Erstellen"}
        </Button>
      </ModalFooter>
    </form>
  );
}

export { MeteringLocationForm, MeteringLocationFormProps };
